import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Filter, Calendar, History, List, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import CEUCard from '../components/ui/CEUCard';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import ceuService from '../services/unifiedCEUService';
import userService from '../services/unifiedUserService';
import { getCurrentCycleCEUs, getPreviousCycleCEUs } from '../utils/cycleTagging';
import { exportCEUsAsCSV } from '../utils/csvExport';

export default function MyCEUs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ceus, setCeus] = useState([]);
  const [filteredCeus, setFilteredCeus] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeTab, setActiveTab] = useState('current');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportScope, setExportScope] = useState('current'); // 'current', 'previous', 'all'

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [ceus, searchTerm, categoryFilter, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const userResult = await userService.getUser(user.userId);
      if (userResult.success) {
        setUserProfile(userResult.data);
      }

      const ceusResult = await ceuService.getCEUs(user.userId);
      if (ceusResult.success) {
        setCeus(ceusResult.data || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setCeus([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...ceus];

    // Filter by cycle tab
    if (activeTab === 'current') {
      filtered = getCurrentCycleCEUs(filtered);
    } else if (activeTab === 'previous') {
      filtered = getPreviousCycleCEUs(filtered);
    }
    // 'all' tab shows everything

    if (searchTerm) {
      filtered = filtered.filter(ceu =>
        ceu.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ceu.instructor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ceu.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(ceu => ceu.category === categoryFilter);
    }

    setFilteredCeus(filtered);
  };

  const handleDeleteCEU = (ceu) => {
    setConfirmDelete(ceu);
  };

  const confirmDeleteCEU = async () => {
    if (!confirmDelete) return;

    try {
      const ceuId = confirmDelete.ceuId || confirmDelete.id;
      const result = await ceuService.deleteCEU(user.userId, ceuId);
      
      if (result.success) {
        setCeus(ceus.filter(c => (c.ceuId || c.id) !== ceuId));
        setConfirmDelete(null);
      } else {
        console.error('Delete failed:', result);
        alert('Failed to delete CEU: ' + (result.error || 'Unknown error'));
        setConfirmDelete(null);
      }
    } catch (err) {
      console.error('Error deleting CEU:', err);
      alert('Failed to delete CEU: ' + err.message);
      setConfirmDelete(null);
    }
  };

  const handleExport = () => {
    let ceusToExport = [];
    
    if (exportScope === 'current') {
      ceusToExport = getCurrentCycleCEUs(ceus);
    } else if (exportScope === 'previous') {
      ceusToExport = getPreviousCycleCEUs(ceus);
    } else {
      ceusToExport = ceus;
    }
    
    exportCEUsAsCSV(ceusToExport, userProfile);
    setShowExportDialog(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My CEUs</h1>
            <p className="text-gray-600 mt-1">
              {ceus.length} total workshop{ceus.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowExportDialog(true)}
              disabled={ceus.length === 0}
            >
              <Download className="w-5 h-5 mr-2" />
              Export CSV
            </Button>
            <Button variant="primary" onClick={() => navigate('/add-ceu')}>
              <Plus className="w-5 h-5 mr-2" />
              Add CEU
            </Button>
          </div>
        </div>

        {/* Cycle Tabs */}
        <Card>
          {/* Desktop tabs */}
          <div className="border-b border-gray-200 hidden md:block">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('current')}
                className={`${
                  activeTab === 'current'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Current Cycle
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === 'current' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {getCurrentCycleCEUs(ceus).length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('previous')}
                className={`${
                  activeTab === 'previous'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <History className="w-4 h-4 mr-2" />
                Previous Cycles
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === 'previous' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {getPreviousCycleCEUs(ceus).length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`${
                  activeTab === 'all'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <List className="w-4 h-4 mr-2" />
                All Cycles
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === 'all' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {ceus.length}
                </span>
              </button>
            </nav>
          </div>

          {/* Mobile stacked buttons */}
          <div className="md:hidden p-4 space-y-2">
            <button
              onClick={() => setActiveTab('current')}
              className={`${
                activeTab === 'current'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } w-full py-3 px-4 rounded-lg font-medium text-sm flex items-center justify-between`}
            >
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Current Cycle
              </div>
              <span className={`py-0.5 px-2 rounded-full text-xs ${
                activeTab === 'current' ? 'bg-indigo-500' : 'bg-gray-200 text-gray-600'
              }`}>
                {getCurrentCycleCEUs(ceus).length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('previous')}
              className={`${
                activeTab === 'previous'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } w-full py-3 px-4 rounded-lg font-medium text-sm flex items-center justify-between`}
            >
              <div className="flex items-center">
                <History className="w-4 h-4 mr-2" />
                Previous Cycles
              </div>
              <span className={`py-0.5 px-2 rounded-full text-xs ${
                activeTab === 'previous' ? 'bg-indigo-500' : 'bg-gray-200 text-gray-600'
              }`}>
                {getPreviousCycleCEUs(ceus).length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`${
                activeTab === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } w-full py-3 px-4 rounded-lg font-medium text-sm flex items-center justify-between`}
            >
              <div className="flex items-center">
                <List className="w-4 h-4 mr-2" />
                All Cycles
              </div>
              <span className={`py-0.5 px-2 rounded-full text-xs ${
                activeTab === 'all' ? 'bg-indigo-500' : 'bg-gray-200 text-gray-600'
              }`}>
                {ceus.length}
              </span>
            </button>
          </div>
        </Card>

        {/* Filters */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Filters</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Search"
                placeholder="Search by title, instructor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              
              <Select
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                options={[
                  { value: '', label: 'All Categories' },
                  { value: 'Professional Studies', label: 'Professional Studies' },
                  { value: 'General Studies', label: 'General Studies' },
                  { value: 'PPO Activities', label: 'PPO Activities' },
                ]}
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setSearchTerm('');
                    setCategoryFilter('');
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* CEU Grid */}
        {filteredCeus.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-600">
                {ceus.length === 0 
                  ? 'No CEUs yet. Click "Add CEU" to get started.'
                  : activeTab === 'current'
                  ? 'No CEUs in your current cycle yet.'
                  : activeTab === 'previous'
                  ? 'No CEUs from previous cycles.'
                  : 'No CEUs match your filters.'}
              </p>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCeus.map((ceu) => (
              <CEUCard
                key={`${ceu.userId || user?.userId}-${ceu.ceuId || ceu.id}`}
                ceu={ceu}
                userProfile={userProfile}
                showCycleIndicator={activeTab === 'all'}
                onEdit={(ceu) => navigate(`/edit-ceu/${ceu.ceuId || ceu.id}`)}
                onDelete={handleDeleteCEU}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={confirmDeleteCEU}
        title="Delete CEU"
        message={`Are you sure you want to delete "${confirmDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Export Dialog */}
      <ConfirmDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onConfirm={handleExport}
        title="Export CEUs to CSV"
        confirmText="Export"
        variant="primary"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select which CEUs to include in your export:
          </p>
          
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="exportScope"
                value="current"
                checked={exportScope === 'current'}
                onChange={(e) => setExportScope(e.target.value)}
                className="w-4 h-4 text-primary"
              />
              <div>
                <div className="font-medium">Current Cycle Only</div>
                <div className="text-sm text-gray-600">
                  {getCurrentCycleCEUs(ceus).length} CEUs
                </div>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="exportScope"
                value="previous"
                checked={exportScope === 'previous'}
                onChange={(e) => setExportScope(e.target.value)}
                className="w-4 h-4 text-primary"
              />
              <div>
                <div className="font-medium">Previous Cycles Only</div>
                <div className="text-sm text-gray-600">
                  {getPreviousCycleCEUs(ceus).length} CEUs
                </div>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="exportScope"
                value="all"
                checked={exportScope === 'all'}
                onChange={(e) => setExportScope(e.target.value)}
                className="w-4 h-4 text-primary"
              />
              <div>
                <div className="font-medium">All CEUs</div>
                <div className="text-sm text-gray-600">
                  {ceus.length} CEUs (all cycles)
                </div>
              </div>
            </label>
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
}
